import React, { useState, useEffect, useCallback } from "react";
import * as R from "ramda";
import { connect } from "react-redux";
import { Row, Spin } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { compose } from "recompose";

import api from "../../utils/api";
import Button from "../Button";
import { ACCEPT_LANGUAGE } from "../../locales/acceptedLanguages";

import styled from "styled-components/macro";

const Wrapper = styled.div`
  text-align: center;
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StatusMessage = styled.p`
  margin: 0.5em 0px;
  width: 390px;
  background: #ffdd502b;
  border-radius: 5px;
  border: 1px solid #dcdc35;
  padding: 10px;
`;

const statuses = {
  loading: "loading",
  sent: "sent",
  accepted: "accepted",
  rejected: "rejected",
  error: "error"
};

const statusThemes = {
  [statuses.accepted]: {
    background: "#5bff502b",
    borderColor: "#47dc35"
  },
  [statuses.rejected]: {
    background: "#ff50502b",
    borderColor: "#dc3535"
  },
  [statuses.error]: {
    background: "#ff50502b",
    borderColor: "#dc3535"
  }
};

function JobOfferMessage({ message, companyId }) {
  const widgetMetadata = R.prop("widgetMetadata", message);
  const { t, i18n } = useTranslation(["opportunity"]);

  const locale = ACCEPT_LANGUAGE[i18n.language] || ACCEPT_LANGUAGE.DEFAULT;
  moment.locale(locale);

  const { idOffer, idOpportunity, pdf } = widgetMetadata;
  const [status, setStatus] = useState(statuses.loading);
  const [jobOffer, setJobOffer] = useState(null);

  const fetchOffer = useCallback(async () => {
    try {
      setStatus(statuses.loading);

      const jobOffer = await api.jobOffer.fetchStatus(
        idOffer,
        idOpportunity,
        companyId
      );
      const { status } = jobOffer;

      setJobOffer(jobOffer);
      if (status) {
        setStatus(status);
      }
    } catch (e) {
      setStatus(statuses.error);
      console.error("Error fetching job offer", e);
    }
  }, [companyId, idOffer, idOpportunity]);
  useEffect(() => {
    fetchOffer();
  }, [fetchOffer]);

  if (!widgetMetadata) return null;

  return (
    <Wrapper>
      {pdf && (
        <Button
          href={pdf}
          download="offer_letter.pdf"
          target="_blank"
          size="lg"
          style={{
            width: 270,
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          icon="eye"
          type="dashed"
        >
          {t("opportunity:viewOffer")}
        </Button>
      )}
      {status === statuses.loading && <Spin style={{ margin: "1em 0" }} />}
      {(status === statuses.accepted ||
        status === statuses.rejected ||
        status === statuses.error ||
        status === statuses.sent) && (
        <Row>
          <StatusMessage style={statusThemes[status]}>
            {status === statuses.accepted
              ? `${t("opportunity:status.accepted")} ${
                  R.prop("timestamp", jobOffer)
                    ? ` ${t("opportunity:day")} ${moment(
                        jobOffer.timestamp
                      ).format("LL")}`
                    : ""
                }`
              : status === statuses.rejected
              ? `${t("opportunity:status.rejected")} ${
                  R.prop("timestamp", jobOffer)
                    ? ` ${t("opportunity:day")} ${moment(
                        jobOffer.timestamp
                      ).format("LL")}`
                    : ""
                }${
                  R.prop("reason", jobOffer)
                    ? ` ${t("opportunity:because")} ${R.prop(
                        "reason",
                        jobOffer
                      )}`
                    : ""
                }`
              : status === statuses.sent
              ? t("opportunity:status.sent")
              : t("opportunity:status.error")}
          </StatusMessage>
        </Row>
      )}
    </Wrapper>
  );
}

export default compose(
  connect(state => ({
    companyId: R.path(["company", "id"], state)
  }))
)(JobOfferMessage);
